"use client";

import React from "react";
import { ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Compass, MapPin, Search, ShieldCheck, Target, TrendingUp, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
export default function WiganAnalysis() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const keywords = [{
    keyword: "SEO consultant Wigan",
    volume: "480/mo",
    intent: "High Commercial",
    difficulty: "Low-Medium"
  }, {
    keyword: "local SEO services Wigan",
    volume: "390/mo",
    intent: "Transactional",
    difficulty: "Low"
  }, {
    keyword: "digital marketing Wigan",
    volume: "720/mo",
    intent: "Commercial",
    difficulty: "Medium"
  }, {
    keyword: "e-commerce SEO consultant North West",
    volume: "880/mo",
    intent: "High Commercial",
    difficulty: "Medium"
  }];
  const opportunities = [{
    title: "Under-Optimized Google Business Profiles",
    description: "Many local Wigan businesses lack optimized secondary categories, schema markup, and geotagged review signals, leaving high-intent local map pack spots open.",
    icon: MapPin
  }, {
    title: "Generic Service Landing Pages",
    description: "Local competitors rely on thin, generic content that fails to capture high-value sub-regional search intent across Wigan and Greater Manchester fringes.",
    icon: Target
  }, {
    title: "Technical Site Performance Gaps",
    description: "Sluggish core web vitals and non-optimized mobile infrastructure cause local businesses to lose potential clients to national directory aggregators.",
    icon: Zap
  }, {
    title: "Untapped Local Authority Links",
    description: "SMEs in Wigan rarely leverage authentic regional partnerships or targeted local digital PR, making rank acquisition straightforward with a tailored strategy.",
    icon: ShieldCheck
  }];
  const neighboringCities = [{
    name: "Warrington",
    path: "/LocalSeoConsultantWarringtonPage",
    desc: "Commercial & B2B SEO Strategy"
  }, {
    name: "Stoke on Trent",
    path: "/LocalSeoConsultantStokeOnTrentPage",
    desc: "Local Search & Technical Optimization"
  }, {
    name: "Derby",
    path: "/LocalSeoConsultantDerbyPage",
    desc: "E-Commerce & National SEO Consultancy"
  }, {
    name: "Wakefield",
    path: "/LocalSeoConsultantWakefieldPage",
    desc: "Lead Generation & Local Visibility"
  }, {
    name: "Wolverhampton",
    path: "/LocalSeoConsultantWolverhamptonPage",
    desc: "Targeted Regional Organic Search"
  }, {
    name: "Hull",
    path: "/LocalSeoConsultantHullPage",
    desc: "Custom Technical & Audits Strategy"
  }];
  return <section id="wiganmarketanalysis" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide">
            Regional Search Intelligence
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Wigan SEO & Local Market Search Analysis
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            I evaluate live Wigan search trends and competitor gaps to help local businesses outrank national aggregators. Here is the local search landscape breakdown and high-growth opportunities I have identified for Wigan.
          </p>
        </motion.div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-stretch">
          {}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInVariants} className="lg:col-span-5 flex flex-col">
            <Card className="h-full border border-border shadow-sm bg-card hover:border-primary/30 transition-[border-color,box-shadow] duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <TrendingUp className="size-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Search Trends Overview
                  </span>
                </div>
                <CardTitle className="text-2xl font-bold font-serif text-card-foreground">
                  Wigan Search Landscape
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  High local buyer intent with low-to-medium organic competition.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 flex-1 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-muted/60 border border-border">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-foreground">Local Intent Search Growth</span>
                      <span className="text-sm font-bold text-primary">+34% YoY</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Mobile searches for Wigan service providers show steady expansion.
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-muted/60 border border-border">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-foreground">Aggregator SERP Dominance</span>
                      <span className="text-sm font-bold text-accent font-semibold">Declining</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Google updates favor dedicated local experts over generic directories in Wigan searches.
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-muted/60 border border-border">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-foreground">Conversion Potential</span>
                      <span className="text-sm font-bold text-primary">High Commercial</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Users searching Wigan keywords show immediate commercial intent to hire or purchase.
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-border">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-md bg-primary/10 text-primary">
                      <BarChart3 className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">Direct Consultant Execution</p>
                      <p className="text-xs text-muted-foreground">You work directly with me to capture these Wigan search volumes.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInVariants} className="lg:col-span-7 flex flex-col">
            <Card className="h-full border border-border shadow-sm bg-card hover:border-primary/30 transition-[border-color,box-shadow] duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <Search className="size-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Target Keywords
                  </span>
                </div>
                <CardTitle className="text-2xl font-bold font-serif text-card-foreground">
                  High-Value Wigan Search Terms
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Sample search volumes and intent profile for Wigan organic search opportunities.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border text-xs uppercase text-muted-foreground font-semibold">
                        <th className="py-3 px-2">Target Keyword</th>
                        <th className="py-3 px-2">Monthly Vol</th>
                        <th className="py-3 px-2">Search Intent</th>
                        <th className="py-3 px-2 text-right">Difficulty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {keywords.map((item, i) => <tr key={item.keyword} data-index={i} className="hover:bg-muted/40 transition-colors">
                          <td className="py-3.5 px-2 font-medium text-foreground flex items-center gap-2">
                            <CheckCircle2 className="size-4 text-primary shrink-0" />
                            <span>{item.keyword}</span>
                          </td>
                          <td className="py-3.5 px-2 text-muted-foreground font-mono text-xs">{item.volume}</td>
                          <td className="py-3.5 px-2">
                            <Badge variant="outline" className="text-xs font-normal">
                              {item.intent}
                            </Badge>
                          </td>
                          <td className="py-3.5 px-2 text-right">
                            <span className="text-xs font-semibold text-primary">{item.difficulty}</span>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 rounded-lg bg-secondary/50 border border-border">
                  <h3 className="text-sm font-semibold text-foreground mb-1">My Technical Takeaway for Wigan</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    By implementing structured GEO entities and localized content hubs, we can secure page-one rankings for high-intent search terms within 3 to 6 months of active consultancy.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground mb-3">
              Key Organic Search Opportunities in Wigan
            </h3>
            <p className="text-sm text-muted-foreground">
              Strategic areas where local businesses can gain an immediate competitive advantage over existing market competitors.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {opportunities.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.title} data-index={i} variants={fadeInVariants}>
                  <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                    <CardHeader className="pb-2">
                      <div className="p-2.5 rounded-md bg-primary/10 text-primary w-fit mb-3">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-base font-bold text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </motion.div>

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="p-8 md:p-10 rounded-2xl bg-card border border-border shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-primary mb-2">
                <Compass className="size-5" />
                <span className="text-xs font-semibold uppercase tracking-wider">
                  Regional Geographic Coverage
                </span>
              </div>
              <h3 className="text-2xl font-bold font-serif text-card-foreground">
                Serving Wigan & Neighboring Regional Hubs
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Explore dedicated local SEO consultancy strategies across surrounding North West and Midlands hubs:
              </p>
            </div>
            <Button asChild variant="default" className="shrink-0">
              <Link to="/ContactPage">
                <span>Book a Wigan SEO Audit</span>
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringCities.map((city, i) => <div key={city.name} data-index={i}>
                <Link to={city.path} className="group block p-4 rounded-lg bg-background border border-border hover:border-primary hover:bg-muted/50 transition-[background-color,border-color] duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                        SEO Consultant {city.name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{city.desc}</p>
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-[color,transform] shrink-0 ml-2" />
                  </div>
                </Link>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}